import { ElementProps } from '@/components/sections/builder/types';
import { ProductDetailVariantPickerElementDataType } from '@/components/sections/builder/elements/body/product-detail/type';
import { Shirt } from 'lucide-react';
import { shallow } from 'zustand/vanilla/shallow';
import { IProduct, ProductAssets, ProductItem } from '@/types/product';
import { useEffect, useMemo, useState } from 'react';

import { ThemeType, useBuilderStore } from '@/stores/builder';
import { useProductStore } from '@/stores/product';
import { useCartProduct } from '@/stores/cart';
import { cn } from '@/lib/utils';
import { PillsPicker } from '@/components/sections/builder/elements/body/product-detail/variant-picker/components/pills-picker';
import { DropdownPicker } from '@/components/sections/builder/elements/body/product-detail/variant-picker/components/dropdown-picker';
import SchemeSetting from '@/components/sections/builder/settings/scheme-setting';
import { DEFAULT_THEME_COLOR_SCHEME } from '@/components/sections/builder/theme/color';

export type VariantType = {
  label: string;
  variant_id: string;
  options: { value: string; label: string; option_id: string }[];
};

const defaultData: ProductDetailVariantPickerElementDataType = {
  name: 'Variant Picker',
  style: 'pills',
  colorScheme: DEFAULT_THEME_COLOR_SCHEME,
};

export const ElementContent = ({ element, preview }: ElementProps) => {
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
  const [hydrated, setHydrated] = useState(false);
  const [initDone, setInitDone] = useState(false);

  const themeBuilder = useBuilderStore((s) => s.theme, shallow);
  const cartProduct = useCartProduct(
    (s) => ({
      item_name: s.item_name,
      quantity: s.quantity,
      clear: s.clear,
      setQuantity: s.setQuantity,
      setVariant: s.setVariant,
    }),
    shallow
  );
  const productStore = useProductStore(
    (s) => ({
      reset: s.reset,
      imageVariant: s.imageVariant,
      setImageVariant: s.setImageVariant,
      setQuantity: s.setQuantity,
      setPrice: s.setPrice,
      setDiscountPrice: s.setDiscountPrice,
      setAllSelectedVariants: s.setAllSelectedVariants,
    }),
    shallow
  );

  const parentData = useMemo(() => (element.parentData || {}) as IProduct, [element.parentData]);
  const content = useMemo(
    () => (element.data || {}) as ProductDetailVariantPickerElementDataType,
    [element.data]
  );

  const variants = useMemo(
    () => (parentData.variants || []) as unknown as VariantType[],
    [parentData]
  );
  const items = useMemo(
    () => (parentData.product_items || []) as unknown as ProductItem[],
    [parentData]
  );

  const theme = preview ? themeBuilder : ((element.theme || {}) as ThemeType);

  useEffect(() => {
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!variants || variants.length === 0) {
      setInitDone(true);
      return;
    }

    const empty = Object.fromEntries(variants.map((v) => [v.variant_id, '']));
    setSelectedOptions(empty);
    setInitDone(true);
  }, [variants, theme.variantPill?.style]);

  const handleSelectVariant = (variantId: string, optionId: string) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [variantId]: prev[variantId] === optionId ? '' : optionId,
    }));
  };

  const allSelectedVariants = useMemo(() => {
    return variants.every((variant) => selectedOptions[variant.variant_id]);
  }, [variants, selectedOptions]);

  // const getStockBySelectedOptions = (): ProductItem | undefined => {
  //   const selectedCombination = Object.values(selectedOptions).filter(Boolean).join(':');
  //   const matchingItem = items.find((item) => item.item_option_id === selectedCombination);
  //   return matchingItem;
  // };

  const productItem = useMemo(() => {
    const selectedCombination = Object.values(selectedOptions).filter(Boolean).join(':');
    return items.find((item) => item.item_option_id === selectedCombination);
  }, [selectedOptions, items]);

  // useEffect(() => {
  //   const emptySelectedOptions = Object.fromEntries(variants.map((v) => [v.variant_id, '']));
  //   setSelectedOptions(emptySelectedOptions);
  // }, [theme.variantPill?.style]);

  useEffect(() => {
    const hasDashOption = Object.values(selectedOptions).includes('');

    if (hasDashOption) {
      productStore.reset();
      cartProduct.clear();
      return;
    }

    if (!productItem) return;

    productStore.setQuantity(productItem.stock || 0);
    productStore.setPrice(productItem.sell_price || 0);
    productStore.setDiscountPrice(productItem.discount_price || 0);
    productStore.setImageVariant((productItem.images as ProductAssets[]) || []);

    cartProduct.setVariant({
      ...productItem,
      variants: productItem.variants,
    });
  }, [productItem]);

  useEffect(() => {
    productStore.setAllSelectedVariants(!!allSelectedVariants);
  }, [allSelectedVariants]);

  const renderReady = hydrated && initDone;

  if (!renderReady) {
    return null;
  }

  return (
    <div className={cn('flex w-full max-w-full', variants.length > 0 && 'border-t pt-4')}>
      {theme.variantPill?.style === 'pills' ? (
        <PillsPicker
          product={parentData}
          variants={variants || []}
          items={items || []}
          selectedOptions={selectedOptions}
          handleSelect={handleSelectVariant}
          colorScheme={content.colorScheme}
          theme={theme.variantPill}
        />
      ) : (
        <div className='w-full'>
          <DropdownPicker
            product={parentData}
            variants={variants || []}
            items={items || []}
            selectedOptions={selectedOptions}
            handleSelect={handleSelectVariant}
          />
        </div>
      )}
    </div>
  );
};

export const ElementPreviewIcon = Shirt;

export function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
    }),
    shallow
  );

  const data = (element.data || defaultData) as ProductDetailVariantPickerElementDataType;

  return (
    <div>
      <h1 className='mb-3 text-sm font-bold'>Style</h1>
      <div className='mb-3 flex flex-col gap-2'>
        <h4>Color Scheme</h4>
        <SchemeSetting
          value={data.colorScheme ?? DEFAULT_THEME_COLOR_SCHEME}
          onChange={(theme) => {
            builder.setChildData(element.parentId as string, element.id, {
              colorScheme: theme,
            });
          }}
        />
      </div>
    </div>
  );
}

const ProductDetailVariantPicker = {
  type: 'variant_picker',
  group: 'product_detail',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
};

export default ProductDetailVariantPicker;
